import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Footer, Navbar } from "../components";
import { useDispatch } from "react-redux";
import { setCredentials } from "../redux/action/authSlice";
import toast from "react-hot-toast";
import api from "../utils/api";

// Eye icons for show/hide password
const IconEye = ({ open }) =>
  open ? (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  ) : (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M1 1l22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password_confirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const searchParams = new URLSearchParams(window.location.search);
  const redirectParam = searchParams.get("redirect");

  const handleGoogleRegister = async () => {
    setGoogleLoading(true);
    try {
      const res = await api.get(
        `/api/auth/google/url${redirectParam ? "?ref=" + encodeURIComponent(redirectParam) : ""}`
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No redirect URL");
      }
    } catch (err) {
      toast.error("Gagal terhubung ke Google OAuth. Coba lagi.");
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== password_confirmation) {
      return toast.error("Password dan konfirmasi password tidak cocok");
    }
    if (password.length < 8) {
      return toast.error("Password minimal 8 karakter");
    }

    setLoading(true);

    try {
      const res = await api.post("/api/auth/register", {
        name,
        email,
        password,
        password_confirmation,
      });
      const data = res.data;

      dispatch(setCredentials({ user: data.user, token: data.token }));
      toast.success(data.message || "Registrasi berhasil!");

      if (redirectParam) {
        navigate(redirectParam, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      let errorMsg = err.message || "Terjadi kesalahan saat registrasi.";
      if (err.response?.data?.errors) {
        errorMsg = Object.values(err.response.data.errors)[0][0];
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Password strength indicator
  const getStrength = (pw) => {
    if (!pw) return 0;
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return score;
  };
  const strength = getStrength(password);
  const strengthLabel = ["", "Lemah", "Cukup", "Baik", "Kuat"][strength];
  const strengthColor = ["", "bg-rose-500", "bg-amber-400", "bg-emerald-400", "bg-emerald-500"][strength];

  return (
    <div className="bg-zinc-950 min-h-screen text-white font-sans selection:bg-emerald-500/30">
      <Navbar />

      <div className="flex flex-col justify-center py-20 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            {/* Logo / Icon */}
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/10 ring-1 ring-emerald-500/30">
              <svg className="h-7 w-7 text-emerald-400" viewBox="0 0 24 24" fill="none">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                <path d="M19 8v6M22 11h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <h2 className="text-3xl font-extrabold tracking-tight">Buat akun baru</h2>
            <p className="mt-2 text-sm text-zinc-400">Bergabung dan nikmati akses ke ribuan produk digital</p>
          </div>

          <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
            <div className="bg-zinc-900 border border-zinc-800 px-6 py-10 shadow-xl sm:rounded-2xl sm:px-12">

              {/* Google Button */}
              <button
                type="button"
                onClick={handleGoogleRegister}
                disabled={googleLoading || loading}
                className="flex w-full items-center justify-center gap-3 rounded-xl bg-white px-3 py-3 text-sm font-semibold text-zinc-900 shadow-sm hover:bg-zinc-100 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {googleLoading ? (
                  <div className="h-5 w-5 border-2 border-zinc-400 border-t-zinc-800 rounded-full animate-spin" />
                ) : (
                  <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                )}
                <span className="text-sm font-semibold leading-6 text-zinc-900">
                  {googleLoading ? "Menghubungkan..." : "Daftar dengan Google"}
                </span>
              </button>

              {/* Divider */}
              <div className="relative mt-8 mb-8">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-zinc-800" />
                </div>
                <div className="relative flex justify-center text-sm font-medium leading-6">
                  <span className="bg-zinc-900 px-6 text-zinc-500 text-xs uppercase tracking-widest">
                    atau daftar dengan email
                  </span>
                </div>
              </div>

              {/* Standard Form */}
              <form className="space-y-5" onSubmit={handleSubmit}>
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium leading-6 text-zinc-300">
                    Nama Lengkap
                  </label>
                  <div className="mt-2">
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="block w-full rounded-xl border-0 bg-white/5 py-2.5 px-4 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-emerald-500 sm:text-sm sm:leading-6 placeholder:text-zinc-600 transition"
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium leading-6 text-zinc-300">
                    Alamat Email
                  </label>
                  <div className="mt-2">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full rounded-xl border-0 bg-white/5 py-2.5 px-4 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-emerald-500 sm:text-sm sm:leading-6 placeholder:text-zinc-600 transition"
                      placeholder="nama@example.com"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium leading-6 text-zinc-300">
                    Password
                  </label>
                  <div className="mt-2 relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full rounded-xl border-0 bg-white/5 py-2.5 pl-4 pr-12 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-emerald-500 sm:text-sm sm:leading-6 placeholder:text-zinc-600 transition"
                      placeholder="Minimal 8 karakter"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors"
                      aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                      tabIndex={-1}
                    >
                      <IconEye open={showPassword} />
                    </button>
                  </div>
                  {/* Strength Bar */}
                  {password && (
                    <div className="mt-2">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4].map((i) => (
                          <div
                            key={i}
                            className={`h-1 flex-1 rounded-full transition-all ${
                              i <= strength ? strengthColor : "bg-zinc-700"
                            }`}
                          />
                        ))}
                      </div>
                      <p className="mt-1 text-xs text-zinc-400">
                        Kekuatan: <span className="font-semibold text-zinc-200">{strengthLabel}</span>
                      </p>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="password_confirmation" className="block text-sm font-medium leading-6 text-zinc-300">
                    Konfirmasi Password
                  </label>
                  <div className="mt-2 relative">
                    <input
                      id="password_confirmation"
                      name="password_confirmation"
                      type={showConfirm ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      value={password_confirmation}
                      onChange={(e) => setPasswordConfirmation(e.target.value)}
                      className={`block w-full rounded-xl border-0 bg-white/5 py-2.5 pl-4 pr-12 text-white shadow-sm ring-1 ring-inset transition sm:text-sm sm:leading-6 placeholder:text-zinc-600 ${
                        password_confirmation && password !== password_confirmation
                          ? "ring-rose-500/70 focus:ring-2 focus:ring-rose-500"
                          : "ring-white/10 focus:ring-2 focus:ring-inset focus:ring-emerald-500"
                      }`}
                      placeholder="Ulangi password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors"
                      aria-label={showConfirm ? "Sembunyikan password" : "Tampilkan password"}
                      tabIndex={-1}
                    >
                      <IconEye open={showConfirm} />
                    </button>
                  </div>
                  {password_confirmation && password !== password_confirmation && (
                    <p className="mt-1 text-xs text-rose-400">Password tidak cocok</p>
                  )}
                </div>

                {/* Submit */}
                <div className="pt-1">
                  <button
                    type="submit"
                    disabled={loading || googleLoading}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 px-3 py-3 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-emerald-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading && (
                      <div className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    )}
                    {loading ? "Membuat akun..." : "Buat Akun"}
                  </button>
                </div>
              </form>

              <p className="mt-8 text-center text-sm text-zinc-400">
                Sudah punya akun?{" "}
                <Link
                  to={redirectParam ? `/login?redirect=${encodeURIComponent(redirectParam)}` : "/login"}
                  className="font-semibold leading-6 text-emerald-400 hover:text-emerald-300 transition-colors"
                >
                  Masuk di sini
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Register;